import { Alert, Box, Stack, Typography } from "@mui/material";
import { parsePhoneNumber } from "libphonenumber-js";
import React, { ReactNode, useCallback, useEffect } from "react";

import { SensitiveData } from "../../utils/highlightUtils";
import { CombinedErrorAlert } from "../combined-error-alert/CombinedErrorAlert";
import { MfaChallenge } from "../mfa-challenge/MfaChallenge";
import { IOfficerInformationFormData } from "./FormData";
import {
    usePhoneVerificationFormCheckPhoneVerificationMutation,
    usePhoneVerificationFormSendPhoneVerificationMutation,
} from "./PhoneVerificationForm.generated";

interface IProps {
    officerData?: IOfficerInformationFormData;
    onSubmit(): void;
    prevPageButton?: ReactNode;
}

export const PhoneVerificationForm: React.FC<IProps> = (props) => {
    const { officerData, prevPageButton } = props;
    if (officerData == null) {
        return (
            <Stack spacing={2}>
                <Alert severity="error">
                    Please go back and fill out the officer information form before continuing.
                </Alert>
                <div>{prevPageButton}</div>
            </Stack>
        );
    }

    return <InnerPhoneVerificationForm {...props} officerData={officerData} />;
};

interface IPropsInner extends IProps {
    officerData: IOfficerInformationFormData;
}

const InnerPhoneVerificationForm: React.FC<IPropsInner> = ({
    officerData: { bcPhoneNumberFull },
    prevPageButton,
    onSubmit,
}) => {
    const phoneNumber = `+${bcPhoneNumberFull}`;

    const [
        { fetching: isSendingVerification, error: sendPhoneVerificationError, data: sendPhoneVerificationResult },
        sendPhoneVerification,
    ] = usePhoneVerificationFormSendPhoneVerificationMutation();
    const [{ fetching: isCheckingVerification, data }, checkPhoneVerification] =
        usePhoneVerificationFormCheckPhoneVerificationMutation();

    useEffect(() => {
        sendPhoneVerification({ phoneNumber });
    }, [phoneNumber, sendPhoneVerification]);

    const handleResend = useCallback((): void => {
        sendPhoneVerification({ phoneNumber });
    }, [phoneNumber, sendPhoneVerification]);

    const handleSubmit = useCallback(
        async (verificationCode: string): Promise<void> => {
            const { data: phoneVericationResult } = await checkPhoneVerification({
                phoneNumber,
                code: verificationCode,
            });

            if (phoneVericationResult?.checkTokenVerification.success) {
                onSubmit();
            }
        },
        [phoneNumber, onSubmit, checkPhoneVerification],
    );

    function renderBody() {
        const formattedPhoneNumber = parsePhoneNumber(phoneNumber).formatInternational();
        return (
            <Typography>
                We are sending an SMS verification code to <SensitiveData>{formattedPhoneNumber}</SensitiveData>. When
                you receive the code please enter it below. If you are having trouble, please go back to update your
                phone number.
            </Typography>
        );
    }

    function renderError() {
        return (
            <CombinedErrorAlert
                title="Failed to send SMS"
                combinedError={sendPhoneVerificationError}
                gqlErrors={sendPhoneVerificationResult?.createTokenVerification.errors ?? undefined}
            />
        );
    }

    return (
        <Stack spacing={2}>
            <Box py={2}>
                <MfaChallenge
                    isChallengeInitializing={isSendingVerification}
                    isChallengeInitialized={sendPhoneVerificationResult?.createTokenVerification.success ?? false}
                    isSubmitting={isCheckingVerification}
                    isCodeInvalid={data != null && !data.checkTokenVerification.success}
                    codeLength={6}
                    renderBody={renderBody}
                    renderError={renderError}
                    onReinitializeChallenge={handleResend}
                    onSubmit={handleSubmit}
                />
            </Box>
            <div>{prevPageButton}</div>
        </Stack>
    );
};
